import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, BookOpen, PlusCircle, Users, CalendarDays, ScanLine,
  BadgeCheck, Gift, UserCog, Activity, Bell, Settings, LogOut, Menu, X, AlertCircle, CreditCard, QrCode,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { HscLogo } from "@/components/HscLogo";
import { cn } from "@/components/kit";

const OWNER_NAV = [
  { to: "/owner/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/owner/ledger", label: "Ledger", icon: BookOpen },
  { to: "/owner/record", label: "Catat Transaksi", icon: PlusCircle },
  { to: "/owner/scan-member", label: "Scan Member", icon: QrCode },
  { to: "/owner/outstanding", label: "Sisa Tagihan", icon: AlertCircle },
  { to: "/owner/members", label: "Members", icon: Users },
  { to: "/owner/classes", label: "Kelas", icon: CalendarDays },
  { to: "/owner/pending-payments", label: "Pending Payments", icon: CreditCard },
  { to: "/owner/checkin", label: "Check-In", icon: ScanLine },
  { to: "/owner/memberships", label: "Membership", icon: BadgeCheck },
  { to: "/owner/rewards", label: "Rewards & Promo", icon: Gift },
  { to: "/owner/team", label: "Tim & Coach", icon: UserCog },
  { to: "/owner/data-health", label: "Data Health", icon: Activity },
  { to: "/owner/settings", label: "Pengaturan", icon: Settings },
];

const ADMIN_NAV = [
  { to: "/admin/ledger", label: "Ledger", icon: BookOpen },
  { to: "/admin/record", label: "Catat Transaksi", icon: PlusCircle },
  { to: "/admin/scan-member", label: "Scan Member", icon: QrCode },
  { to: "/admin/checkin", label: "Check-In", icon: ScanLine },
  { to: "/admin/classes", label: "Kelas", icon: CalendarDays },
  { to: "/admin/pending-payments", label: "Pending Payments", icon: CreditCard },
  { to: "/admin/members", label: "Members", icon: Users },
  { to: "/admin/memberships", label: "Membership", icon: BadgeCheck },
];

// All-access admins get owner-level management (everything EXCEPT the BI Dashboard).
const ADMIN_FULL_NAV = [
  { to: "/admin/ledger", label: "Ledger", icon: BookOpen },
  { to: "/admin/record", label: "Catat Transaksi", icon: PlusCircle },
  { to: "/admin/scan-member", label: "Scan Member", icon: QrCode },
  { to: "/admin/outstanding", label: "Sisa Tagihan", icon: AlertCircle },
  { to: "/admin/members", label: "Members", icon: Users },
  { to: "/admin/classes", label: "Kelas", icon: CalendarDays },
  { to: "/admin/pending-payments", label: "Pending Payments", icon: CreditCard },
  { to: "/admin/checkin", label: "Check-In", icon: ScanLine },
  { to: "/admin/memberships", label: "Membership", icon: BadgeCheck },
  { to: "/admin/rewards", label: "Rewards & Promo", icon: Gift },
  { to: "/admin/team", label: "Tim & Coach", icon: UserCog },
  { to: "/admin/data-health", label: "Data Health", icon: Activity },
  { to: "/admin/settings", label: "Pengaturan", icon: Settings },
];

export default function DashboardShell({ role }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const nav = role === "admin" ? (user?.all_access ? ADMIN_FULL_NAV : ADMIN_NAV) : OWNER_NAV;

  useEffect(() => {
    const tick = () => api.unreadCount().then((r) => setUnread(r.count || 0)).catch(() => {});
    tick();
    const iv = setInterval(tick, 20000);
    return () => clearInterval(iv);
  }, []);

  const doLogout = () => { logout(); navigate("/login", { replace: true }); };

  const NavItems = () => (
    <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-4">
      {nav.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors",
                isActive ? "bg-white text-black" : "text-[#A1A1A6] hover:bg-[#141416] hover:text-white"
              )
            }
          >
            <Icon className="h-[18px] w-[18px]" />
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );

  const SidebarInner = (
    <>
      <div className="flex items-center justify-between border-b border-[#1F1F22] px-5 py-5">
        <HscLogo size="text-xl" />
        <button className="lg:hidden text-white" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
      </div>
      <NavItems />
      <div className="border-t border-[#1F1F22] px-4 py-4">
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#141416] font-display text-sm text-white">
            {(user?.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-white">{user?.name}</div>
            <div className="text-[11px] uppercase tracking-widest text-[#6E6E73]">{role}</div>
          </div>
        </div>
        <button
          onClick={doLogout}
          data-testid="logout-button"
          className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-[#A1A1A6] transition-colors hover:bg-[#141416] hover:text-white"
        >
          <LogOut className="h-4 w-4" /> Keluar
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-[#1F1F22] bg-[#050506] lg:flex">
        {SidebarInner}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-64 flex-col border-r border-[#1F1F22] bg-[#050506]">
            {SidebarInner}
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-[#1F1F22] bg-black/80 px-4 py-3 backdrop-blur lg:px-8">
          <button className="lg:hidden text-white" onClick={() => setOpen(true)}><Menu className="h-6 w-6" /></button>
          <div className="lg:hidden"><HscLogo size="text-lg" /></div>
          <div className="hidden text-sm text-[#6E6E73] lg:block">Panel Manajemen</div>
          <NavLink to={`/${role}/notifications`} className="relative rounded-full p-2 hover:bg-[#141416]" data-testid="topbar-notifications">
            <Bell className="h-5 w-5 text-[#D1D1D6]" />
            {unread > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF6B6B] px-1 text-[10px] font-bold text-white" data-testid="notif-badge">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </NavLink>
        </header>
        <main className="mx-auto max-w-6xl px-4 py-6 lg:px-8 lg:py-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
